/**
 * auroraPipeline — vertical "northern lights" bands.
 *
 * Built from the same ingredients as Beat Nebula (volumetric raymarcher,
 * nested sin layers, space folding, tanh tone mapping) but applied to
 * vertical flowing bands instead of a uniform volumetric nebula.
 *
 * Key differences from Beat Nebula:
 *   - Vertical UV scaling (uv.y is the primary spatial axis)
 *   - sin-based wave amplitude along Y
 *   - Gradient palette (deep navy -> teal -> cyan -> violet -> white)
 *   - Camera "looks up" at the sky (Y-up world)
 *   - Audio drives wave amplitude and color saturation
 *
 * No mesh, no simulation — single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface AuroraPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

// Hash + value noise for sky texture
float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.03 + 7.1;
    a *= 0.5;
  }
  return v;
}

void main() {
  // Audio bands.
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Screen-space UV, centered and aspect-corrected.
  vec2 uv = vUV;
  vec2 p2 = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Camera looks up at the sky. We march from the bottom of the
  // screen upward. The bands appear as horizontal ribbons that flow
  // vertically.
  float t = uTime;

  // Sky background — deep night gradient.
  vec3 sky = mix(
    vec3(0.02, 0.03, 0.08),    // bottom: deep navy
    vec3(0.05, 0.10, 0.18),    // top: lighter blue
    uv.y
  );
  // Star field: tiny bright dots from valueNoise.
  float stars = step(0.997, valueNoise(floor(gl_FragCoord.xy * 0.7) + floor(t * 0.5)));
  sky += vec3(0.9, 0.95, 1.0) * stars * 0.8;

  // Aurora band parameters.
  // We treat the screen as 3 overlapping ribbon layers.
  // Each ribbon is a horizontal wave with vertical falloff.
  vec3 col = sky;

  // Three aurora ribbons with different colors and parameters.
  for (int layer = 0; layer < 3; layer++) {
    float fl = float(layer);
    // Each layer has different vertical center + frequency.
    float yCenter = 0.3 + fl * 0.12;
    float yWidth = 0.18 + fl * 0.04;
    float speed = 0.4 + fl * 0.15;

    // The aurora is a horizontal wave that flows over time.
    float wave = sin(
      p2.x * (3.0 + fl * 1.5)
      + t * speed * (1.5 + fl * 0.4)
      + fbm(p2 * 1.5 + vec2(t * 0.1, 0.0)) * 4.0
    );

    // Vertical falloff: bright at yCenter, fading away.
    float yDist = abs(p2.y - (yCenter - 0.1 + wave * 0.05));
    float band = smoothstep(yWidth, 0.0, yDist);

    // Audio modulation.
    float audioMod = 0.6 + 0.4 * bass * (1.0 - fl * 0.2);

    // Color per layer.
    vec3 ribbonColor;
    if (layer == 0) {
      // Cyan/teal — the bottom band.
      ribbonColor = vec3(0.1, 0.7, 0.6);
    } else if (layer == 1) {
      // Violet/purple — middle.
      ribbonColor = vec3(0.4, 0.2, 0.85);
    } else {
      // Magenta/pink — top.
      ribbonColor = vec3(0.95, 0.3, 0.6);
    }
    // Mix toward white at the brightest peaks.
    ribbonColor = mix(ribbonColor, vec3(0.95, 0.95, 1.0), high * 0.5);

    col += ribbonColor * band * audioMod * (0.7 + high * 0.6);
  }

  // Vertical light haze rising from the bottom (sky glow).
  float haze = exp(-p2.y * 1.5) * 0.05;
  col += vec3(0.05, 0.15, 0.25) * haze;

  // Subtle vertical streaks (rays) — high-audio accent.
  float rays = step(0.985, fract(sin(p2.x * 30.0 + t * 0.5) * 100.0));
  col += vec3(0.6, 0.8, 1.0) * rays * high * 0.3;

  // Tonemap (ACES-approximation).
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Slight vignette.
  float vig = 1.0 - dot(p2 * 0.7, p2 * 0.7) * 0.25;
  col *= clamp(vig, 0.0, 1.0);

  outColor = vec4(col, 1.0);
}
`;

/**
 * Synthesize 9 frequency-band values tuned for aurora — slow
 * ambient feel, less rhythmic than Beat/Hex.
 */
export function computeAuroraBands(t: number, out: Float32Array): void {
  // Very slow, ambient patterns.
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.3);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.5 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 0.7 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 0.9 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.1 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.4 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 1.8 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.2 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 2.8 + 3.0);
}

export function computeAuroraRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  // Same 9-band split as Hex (low/mid/high three each).
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030],
    [0.030, 0.080],
    [0.080, 0.150],
    [0.150, 0.260],
    [0.260, 0.380],
    [0.380, 0.500],
    [0.500, 0.660],
    [0.660, 0.820],
    [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createAuroraPipeline(gl: WebGL2RenderingContext): AuroraPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawAurora(
  gl: WebGL2RenderingContext,
  pipe: AuroraPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
